import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import styled from '@emotion/styled';
import { observable, action } from 'mobx';
import SearchBars from 'components/SearchBars';
import { observer, inject } from 'mobx-react';
import {
  Button, Intent, Dialog, Classes, Checkbox
} from '@blueprintjs/core';
import Card from 'components/Card';
import * as DataList from 'components/DataList';

import Toast from 'helpers/toaster';
import ProjectSidebar from '../projects/project-sidebar';

const PageWrapGray = styled('div')`
  padding: 12px;
  background-color: #F8FAFB;
  min-height: calc(100vh - 75px);
`

// Material orders have no record page of their own, so rows have no RowLink;
// the supplier, work order and purchase order are raised links.
const COLUMNS = [
  { area: 'select', label: '', width: '28px', bone: 'none' },
  { area: 'item', label: 'Item', width: 'minmax(180px, 1.6fr)', bone: 'short' },
  { area: 'supplier', label: 'Supplier', width: 'minmax(150px, 1.1fr)', bone: 'single', icon: 'shop' },
  { area: 'part', label: 'Part number', width: 'minmax(110px, 0.8fr)', bone: 'single', icon: 'barcode' },
  { area: 'qty', label: 'Qty', width: '64px', bone: 'single' },
  { area: 'workOrder', label: 'Work order', width: '110px', bone: 'single', icon: 'clipboard' },
  { area: 'status', label: 'Status', width: '150px', bone: 'pill' },
  { area: 'purchaseOrder', label: 'Purchase order', width: '130px', bone: 'single' },
];

const CARDS = {
  columns: 'auto minmax(0, 1fr) auto',
  areas: [
    'select item status',
    'supplier supplier supplier',
    'part part part',
    'workOrder workOrder workOrder',
    'qty qty purchaseOrder',
  ],
  footer: ['qty', 'purchaseOrder'],
};

const MIN_TABLE_WIDTH = 1060;

const NOUN = 'material orders';

// Defaults from viewStore.materialLineItemSearch.
const DEFAULT_STATUS = ['pending order'];
const DEFAULT_WHERE = {
  work_report_id: '',
  supplier_id: '',
  status: DEFAULT_STATUS,
};

const FILTERS = {
  supplier_id: 'supplier',
  work_report_id: 'work order',
  status: 'status',
};

const STATUS_TONES = {
  'pending order': 'amber',
  ordered: 'blue',
  'in transit': 'purple',
  received: 'green',
  'picked up by the technician': 'green',
  archived: 'grey',
};

const isDefaultStatus = (status) =>
  Array.isArray(status) && status.length === DEFAULT_STATUS.length &&
  DEFAULT_STATUS.every((value) => status.includes(value));

const MaterialStatus = ({ status }) => (
  <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>
    {status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'No status'}
  </DataList.Pill>
);

@inject('store', 'workReportStore', 'viewStore') @observer
class MaterialOrders extends Component {
  @observable isOpen = false;

  // Ids of the material line items picked for the next purchase order.
  @observable selectedIds = [];

  componentDidMount() {
    this.props.workReportStore.loadMaterialLineItems();
  }

  @action
  toggleSelected(event, id) {
    if (event.target.checked) {
      if (this.selectedIds.indexOf(id) === -1) this.selectedIds.push(id);
    } else {
      const index = this.selectedIds.indexOf(id);
      if (index !== -1) this.selectedIds.splice(index, 1);
    }
  }

  updateSelectedResourceForPurchaseOrder() {
    let {
      workReportStore,
      workReportStore: {
        purchaseOrderItem
      }
    } = this.props;

    if (this.selectedIds.length > 0) {
      workReportStore.addPurchaseOrderItem(this.selectedIds.slice())
      // The old list re-mounted its checkboxes on reload, so they came back
      // unticked; clear the selection to match.
      this.selectedIds.clear();
      if (purchaseOrderItem.isError == true) {
        this.FailAddPurchaseOrder();
      } else {
        this.onSucessfullAddPurchaseOrder();
      }
    }
  }

  handleConfirm() {
    this.isOpen = false;
    this.updateSelectedResourceForPurchaseOrder();
  }

  updateSelectedResource() {
    let { objects } = this.props.workReportStore.materialLineItem;

    var supplierData = 0;
    objects &&
      objects.forEach((materialItem) => {
        if (this.selectedIds.indexOf(materialItem.attributes.id) > -1) {
          if (supplierData == 0) {
            supplierData = materialItem.attributes.supplier.id;
          } else {
            if (supplierData != materialItem.attributes.supplier.id) {
              this.isOpen = true;
            }
          }
        }
      });

    if (this.isOpen == false) {
      this.updateSelectedResourceForPurchaseOrder();
    }

    if (this.selectedIds.length === 0) {
      Toast.show({ icon: 'warning-sign', message: 'Please select atleast one material item to create purchase order.', intent: Intent.DANGER })
    }
  }

  onSucessfullAddPurchaseOrder(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Purchase order added successfully!', intent: Intent.SUCCESS })
    this.props.workReportStore.loadMaterialLineItems();
  }

  FailAddPurchaseOrder(error) {
    Toast.show({ icon: 'warning-sign', message: 'Failed to add purchase order', intent: Intent.DANGER })
  }

  handleClose() {
    this.isOpen = false;
  }

  // Empty list's "Clear filters": back to the default search (pending orders),
  // and remount the filter bar so its pickers forget their selections.
  clearFilters = () => {
    const { viewStore, workReportStore } = this.props;
    const { object } = workReportStore.persistMaterial;
    if (object) {
      object.supplier_id = null;
      object.work_report_id = null;
    }
    viewStore.materialLineItemSearch = {
      ...viewStore.materialLineItemSearch,
      where: { ...DEFAULT_WHERE, status: DEFAULT_STATUS.slice() },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.MaterialOrders();
    workReportStore.loadMaterialLineItems();
  }

  renderEmpty() {
    const { where = {} } = this.props.viewStore.materialLineItemSearch;
    // Pending orders is the default view, not a filter the user picked.
    const picked = isDefaultStatus(where.status) ? { ...where, status: null } : where;
    const filters = DataList.activeFilters(picked, FILTERS);
    if (filters.length === 0 && isDefaultStatus(where.status)) {
      return (
        <DataList.Empty
          noun={NOUN}
          title="Nothing waiting to be ordered"
          message="Materials added to work orders show up here until they're on a purchase order."
        />
      );
    }
    return (
      <DataList.Empty
        noun={NOUN}
        filters={filters}
        onClearFilters={this.clearFilters}
      />
    );
  }

  renderList() {
    const { viewStore } = this.props;
    const { objects } = this.props.workReportStore.materialLineItem;
    const rows = (objects || []).filter((materialItem) => materialItem.attributes.work_report_id);

    if (rows.length === 0) return this.renderEmpty();

    return (
      <DataList.List
        columns={COLUMNS}
        cards={CARDS}
        minTableWidth={MIN_TABLE_WIDTH}
        total={rows.length}
        noun={NOUN}
        note={DataList.sortNote(viewStore.materialLineItemSearch.order)}
      >
        {rows.map((materialItem) => {
          const attributes = materialItem.attributes;
          const supplier = attributes.supplier || {};
          const workReport = attributes.work_report || {};
          const purchaseOrder = attributes.purchase_order || {};
          const ordered = Boolean(purchaseOrder.number);
          const selected = this.selectedIds.indexOf(attributes.id) > -1;
          return (
            <DataList.Row
              key={materialItem.id || attributes.id}
              className={selected ? 'material-orders__row--selected' : ''}
            >
              <DataList.Cell area="select">
                <Checkbox
                  className="dl-raised"
                  style={{ margin: 0 }}
                  checked={selected}
                  disabled={ordered}
                  onChange={(event) => this.toggleSelected(event, attributes.id)}
                  aria-label={ordered
                    ? `${attributes.description || 'Item'} is already on purchase order ${purchaseOrder.number}`
                    : `Select ${attributes.description || 'item'}`}
                />
              </DataList.Cell>
              <DataList.Cell area="item">
                <DataList.Lines primary={attributes.description || 'Untitled item'} />
              </DataList.Cell>
              <DataList.Cell area="supplier">
                {supplier.id ? (
                  <DataList.Lines
                    primary={supplier.name}
                    href={`/dashboard/suppliers/${supplier.id}/show`}
                  />
                ) : (
                  <DataList.Muted>No supplier</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="part">
                {attributes.part_number ? (
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Part </DataList.CardOnly>{attributes.part_number}</React.Fragment>}
                    title={attributes.part_number}
                    strong={false}
                  />
                ) : (
                  <DataList.Muted>No part number</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="qty">
                <DataList.Lines
                  primary={<React.Fragment><DataList.CardOnly>Qty </DataList.CardOnly>{attributes.quantity}</React.Fragment>}
                  title={`Quantity ${attributes.quantity}`}
                  strong={false}
                />
              </DataList.Cell>
              <DataList.Cell area="workOrder">
                <DataList.Lines
                  primary={<React.Fragment><DataList.CardOnly>Work order </DataList.CardOnly>{workReport.number}</React.Fragment>}
                  href={`/dashboard/work-reports/${workReport.id}/show`}
                  title={`Work order ${workReport.number}`}
                />
              </DataList.Cell>
              <DataList.Cell area="status">
                <MaterialStatus status={attributes.status} />
              </DataList.Cell>
              <DataList.Cell area="purchaseOrder">
                {ordered ? (
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>PO </DataList.CardOnly>{purchaseOrder.number}</React.Fragment>}
                    href={`/dashboard/purchase-orders/${purchaseOrder.id}/show-purchase-order`}
                    title={`Purchase order ${purchaseOrder.number}`}
                  />
                ) : (
                  <DataList.Muted>Not ordered</DataList.Muted>
                )}
              </DataList.Cell>
            </DataList.Row>
          );
        })}
      </DataList.List>
    );
  }

  render() {
    const { workReportStore, viewStore } = this.props;
    const { objects, isLoading, isError } = workReportStore.materialLineItem;

    let content;
    if (isLoading) {
      content = (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun={NOUN} />
      );
    } else if (isError) {
      content = <DataList.LoadError noun={NOUN} onRetry={() => workReportStore.loadMaterialLineItems()} />;
    } else {
      content = this.renderList();
    }

    const selectedCount = this.selectedIds.length;

    return (
      <div>
        <Helmet><title>Material Orders</title></Helmet>
        <div className="row no-gutters">
          <div className="col-lg-2">
            <ProjectSidebar />
          </div>
          <div className="col-lg-10">
            <PageWrapGray>
              <SearchBars.MaterialOrders key={viewStore.listFiltersVersion} />
              {!isLoading && !isError && objects.length > 0 && (
                <div className="material-orders__actions">
                  {selectedCount > 0 && (
                    <span className="material-orders__selected" aria-live="polite">
                      {selectedCount} selected
                    </span>
                  )}
                  <Button
                    icon="plus"
                    text="Create purchase order"
                    intent={Intent.PRIMARY}
                    className="material-orders__create"
                    onClick={this.updateSelectedResource.bind(this)}
                  />
                </div>
              )}
              <Card>
                {content}
              </Card>
            </PageWrapGray>
          </div>
        </div>

        <Dialog
          title="Purchase order"
          onClose={this.handleClose.bind(this)}
          isOpen={this.isOpen}
        >
          <div className={Classes.DIALOG_BODY}>
            You have selected multiple suppliers,this will genrate multiple purchase orders(1 per supplier).
              Are you sure you want to do this?
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.handleClose.bind(this)}>Cancel</Button>
              <Button intent={Intent.PRIMARY} onClick={this.handleConfirm.bind(this)}>Yes,I understand, please proceed</Button>
            </div>
          </div>

        </Dialog>
      </div>
    )
  }

};

export default MaterialOrders;
